package com.crowpay.views.components.nav

import com.crowpay.clients
import com.crowpay.edit
import com.crowpay.finances
import com.crowpay.loggedInAsContractor
import com.crowpay.messageBubble
import com.crowpay.notificationBell
import com.crowpay.projects
import com.crowpay.sdk.ApiOption
import com.crowpay.sdk.notNullSession
import com.crowpay.sdk.selectedApi
import com.crowpay.simpleHome
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.resize
import com.crowpay.views.components.project.activity.ProjectMessageThread
import com.crowpay.views.components.space
import com.crowpay.views.screens.client.ClientDashboard
import com.crowpay.views.screens.client.ClientPayments
import com.crowpay.views.screens.client.ClientProjects
import com.crowpay.views.screens.common.ClientNotificationSettings
import com.crowpay.views.screens.common.ClientWorkLists
import com.crowpay.views.screens.common.ContractorNotificationSettings
import com.crowpay.views.screens.common.ContractorWorkLists
import com.crowpay.views.screens.common.MessagingScreen
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.SelectDevProjectScreen
import com.crowpay.views.screens.contractor.ContractorClients
import com.crowpay.views.screens.contractor.ContractorDashboard
import com.crowpay.views.screens.contractor.ContractorPayments
import com.crowpay.views.screens.contractor.ContractorProjects
import com.crowpay.views.screens.contractor.ManageContractorUsers
import com.crowpay.views.screens.contractor.Specifications
import com.crowpay.views.theming.MobileNavSemantic
import com.crowpay.views.theming.appTheme
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.subTitle
import com.crowpay.workList
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.ButtonSemantic
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.NavSemantic
import com.lightningkite.kiteui.models.PopoverPreferredDirection
import com.lightningkite.kiteui.models.SelectedSemantic
import com.lightningkite.kiteui.models.ThemeDerivation
import com.lightningkite.kiteui.models.UnselectedSemantic
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.minus
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.toggle
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.closePopovers
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.link
import com.lightningkite.kiteui.views.direct.menuButton
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.weight
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.l2.icon

private val mostRecentNavSelection = Property<String?>("Home")

private fun ReactiveContext.contractorOrHomeownerDestination(
    ifContractor: () -> Page,
    ifHomeowner: () -> Page
) = if (loggedInAsContractor()) ifContractor else ifHomeowner


fun ViewWriter.crowpaySideNav() =
    col {
        spacing = 0.dp
        existsDefaultFalse { navExists() }

        space(AppDimensions.fullIndent - 0.75.rem)

        fun ViewWriter.navLink(
            icon: Icon?,
            text: String,
            exists: (ReactiveContext.() -> Boolean)? = null,
            onNavigate: (suspend () -> Unit)? = null,
            destination: ReactiveContext.() -> () -> Page
        ) = link {
            exists?.let {
                existsDefaultFalse(it)
            }
            this.onNavigate {
                onNavigate?.invoke()
                mostRecentNavSelection.value = text
            }
            dynamicTheme {
                if (text == mostRecentNavSelection()) SelectedSemantic else UnselectedSemantic
            }
            row {
                space(0.3)
                icon?.let { centered - this.icon(it.resize(1.5.rem), text) }
                centered - subTitle(text)
                space()
            }
            ::to { destination() }
        }
        fun ViewWriter.contractorLink(
            icon: Icon?,
            text: String,
            onNavigate: (suspend () -> Unit)? = null,
            destination: ReactiveContext.() -> () -> Page
        ) = navLink(icon, text, exists = { loggedInAsContractor() }, onNavigate, destination)

        navLink(Icon.simpleHome, "Home") {
            contractorOrHomeownerDestination(
                { ContractorDashboard() },
                { ClientDashboard() }
            )
        }
        contractorLink(Icon.projects, "Projects") {
            contractorOrHomeownerDestination(
                { ContractorProjects() },
                { ClientProjects() }
            )
        }
        contractorLink(Icon.clients, "Clients") { { ContractorClients() } }
        contractorLink(Icon.workList, "Tasks") {
            contractorOrHomeownerDestination(
                { ContractorWorkLists() },
                { ClientWorkLists() }
            )
        }
        navLink(Icon.messageBubble, "Messages") {
            val lens = if (loggedInAsContractor()) ProjectLens.Contractor else ProjectLens.Customer
            notNullSession().myProjects()
                .singleOrNull()
                ?.let {
                    { ProjectMessageThread(it._id, lens) }
                }
                ?: { MessagingScreen(lens) }
        }
        navLink(Icon.finances, "Finances") {
            contractorOrHomeownerDestination(
                { ContractorPayments() },
                { ClientPayments() }
            )
        }
        navLink(Icon.edit, "Dev Projects", exists = { selectedApi() in ApiOption.SAFE }) {
            { SelectDevProjectScreen() }
        }

        expanding - space()

        val settingsOpen = Property(false)
        onlyWhen { settingsOpen() } - col {
            spacing = 0.rem
            greySeparator()
            navLink(null, "Alerts", onNavigate = { settingsOpen.value = false }) {
                contractorOrHomeownerDestination(
                    { ContractorNotificationSettings() },
                    { ClientNotificationSettings() }
                )
            }
            contractorLink(null, "Specifications", onNavigate = { settingsOpen.value = false }) {
                { Specifications() }
            }
            contractorLink(null, "Users", onNavigate = { settingsOpen.value = false }) {
                { ManageContractorUsers() }
            }
        }
        button {
            row {
                space(0.3)
                centered - icon(Icon.settings, "settings")
                centered - subTitle("Settings")
                space()
            }
            onClick("Toggle Settings") {
                settingsOpen.toggle()
            }
        }
        space(AppDimensions.fullIndent - 1.25.rem)
    }

fun ViewWriter.crowpayBottomNav(): ViewModifiable {
    fun ViewWriter.navLink(
        icon: Icon?,
        text: String,
        exists: (ReactiveContext.() -> Boolean)? = null,
        onNavigate: (suspend () -> Unit)? = null,
        destination: ReactiveContext.() -> () -> Page
    ) = link {
        spacing = 0.dp
        exists?.let {
            existsDefaultFalse(it)
        }
        this.onNavigate {
            onNavigate?.invoke()
            mostRecentNavSelection.value = text
        }
        dynamicTheme {
            if (text == mostRecentNavSelection()) SelectedSemantic else UnselectedSemantic
        }
        col {
            spacing = AppDimensions.expandButtonSpace
            icon?.let { centered - this.icon(it.resize(1.5.rem), text) }
            centered - bodyBold(text)
        }
        ::to { destination() }
    }
    fun ViewWriter.contractorLink(
        icon: Icon?,
        text: String,
        onNavigate: (suspend () -> Unit)? = null,
        destination: ReactiveContext.() -> () -> Page
    ) = navLink(icon, text, exists = { loggedInAsContractor() }, onNavigate, destination)

    return row {
        spacing = AppDimensions.buttonSpacing
        existsDefaultFalse { navExists() }
        weight(0.9f) - navLink(Icon.simpleHome, "Home") {
            contractorOrHomeownerDestination(
                { ContractorDashboard() },
                { ClientDashboard() }
            )
        }
        weight(1.0f) - contractorLink(Icon.projects, "Projects") {
            contractorOrHomeownerDestination(
                { ContractorProjects() },
                { ClientProjects() }
            )
        }
        weight(0.8f) - navLink(Icon.notificationBell, "Alerts") {
            { UserNotifications }
        }
        weight(1.0f) - navLink(Icon.messageBubble, "Messages") {
            val lens = if (loggedInAsContractor()) ProjectLens.Contractor else ProjectLens.Customer
            notNullSession().myProjects()
                .singleOrNull()
                ?.let {
                    { ProjectMessageThread(it._id, lens) }
                }
                ?: { MessagingScreen(lens) }
        }
        weight(0.9f) - UnselectedSemantic.onNext - menuButton {
            spacing = 0.dp
            preferredDirection = PopoverPreferredDirection.aboveLeft

            col {
                spacing = AppDimensions.expandButtonSpace
                centered - icon(Icon.menu, "menu")
                bodyBold {
                    align = Align.Center
                    content = "More"
                }
            }

            opensMenu {
                val settingsOpen = Property(false)

                sizeConstraints(width = 8.rem) - (ThemeDerivation.Set(appTheme) + MobileNavSemantic + NavSemantic).onNext - col {
                    spacing = 0.dp
                    fun ViewWriter.menuLink(icon: Icon?, name: String, exists: (ReactiveContext.() -> Boolean)? = null, to: ReactiveContext.() -> () -> Page) =
                        buttonTheme - link {
                            if (icon != null) row {
                                centered - icon(icon.resize(1.5.rem), name)
                                centered - bodyBold(name)
                            }
                            else body(name)

                            exists?.let { this::exists.invoke(it) }
                            onNavigate {
                                mostRecentNavSelection.value = name
                                closePopovers()
                            }
                            dynamicTheme {
                                if (name == mostRecentNavSelection()) SelectedSemantic else UnselectedSemantic
                            }
                            this::to.invoke(to)
                        }

                    fun ViewWriter.conMenuLink(icon: Icon?, name: String, to: ReactiveContext.() -> () -> Page) = menuLink(icon, name, exists = { loggedInAsContractor() }, to)

                    conMenuLink(Icon.clients, "Clients") { { ContractorClients() } }
                    greySeparator()
                    conMenuLink(Icon.workList, "Tasks") {
                        contractorOrHomeownerDestination(
                            { ContractorWorkLists() },
                            { ClientWorkLists() }
                        )
                    }
                    greySeparator()
                    menuLink(Icon.finances, "Finances") {
                        contractorOrHomeownerDestination(
                            { ContractorPayments() },
                            { ClientPayments() }
                        )
                    }
                    greySeparator()
                    onlyWhen { settingsOpen() } - col {
                        spacing = AppDimensions.expandButtonSpace
                        menuLink(null, "Alerts") {
                            contractorOrHomeownerDestination(
                                { ContractorNotificationSettings() },
                                { ClientNotificationSettings() }
                            )
                        }
                        conMenuLink(null, "Specifications") {
                            { Specifications() }
                        }
                        conMenuLink(null, "Users") {
                            { ManageContractorUsers() }
                        }
                    }
                    (ButtonSemantic + UnselectedSemantic).onNext - button {
                        row {
                            centered - icon(Icon.settings.resize(1.5.rem), "settings")
                            centered - bodyBold("Settings")
                        }

                        onClick {
                            settingsOpen.toggle()
                        }
                    }
                }
            }
        }
    }
}